// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { all, call, delay, put, select, takeEvery } from 'redux-saga/effects';
import { AgGridUtils } from '@cosmotech/core';
import { Api } from '../../../services/config/Api';
import { TWINGRAPH_QUERIES_DELAY } from '../../../services/config/FunctionalConstants';
import WorkspaceService from '../../../services/workspace/WorkspaceService';
import { DatasetsUtils } from '../../../utils';
import { WizardUtils } from '../../../utils/WizardUtils';
import { WIZARD_TWINGRAPH_QUERIES } from '../WizardConstants';

const getOrganizationId = (state) => state.organization.current?.data?.id;
const getSolutionData = (state) => state.solution.current?.data;
const getWorkspaceId = (state) => state.workspace.current?.data?.id;
const getDatasets = (state) => state.dataset.list?.data;

function* getOneQuery(datasetId, scenarioId, query) {
  const organizationId = yield select(getOrganizationId);
  try {
    const { data } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, { query: query.queryString });
    let activityDetails;
    if (query.key === 'ClasseActif') {
      activityDetails = [...data];

      const { data: valuesData } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
        query: WizardUtils.buildMetadataQueryString(query.value),
      });

      activityDetails[0].Values = WizardUtils.getFilteredMetadata(valuesData);
    } else {
      activityDetails = data.map((r) => (query.usePropertyKey ? r.propertyKey : r.n.properties));
    }

    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_TECHNICAL_POLICY_TWINGRAPH_QUERIES,
      payload: { result: activityDetails, scenarioId, key: query.key, gisementName: query.gisementName },
    });
  } catch (e) {
    console.error(e);
  }
}

function* downloadFileData(scenarioId, file) {
  const organizationId = yield select(getOrganizationId);
  const workspaceId = yield select(getWorkspaceId);
  const datasets = yield select(getDatasets);
  const dataset = datasets?.find((dataset) => dataset.id === file.value);
  if (!dataset) {
    console.warn(`Dataset ${file.value} not found in the dataset list`);
    return;
  }
  const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
  const data = yield call(WorkspaceService.downloadWorkspaceFileData, organizationId, workspaceId, storageFilePath);
  const fileContentFirstLine = data.split('\n')[0].split(',');
  const columns = fileContentFirstLine.map((col) => ({ field: col, acceptsEmptyFields: true }));
  const parsedData = AgGridUtils.fromCSV(data, true, columns);
  if (file.gisementName) {
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_TECHNICAL_POLICY_TWINGRAPH_QUERIES,
      payload: { scenarioId, result: parsedData.rows, key: file.key, gisementName: file.gisementName },
    });
  }
  return parsedData.rows;
}

function* getGanttChartTwingraphQueries(action) {
  const organizationId = yield select(getOrganizationId);
  const solutionData = yield select(getSolutionData);
  const technicalPolicyParameter = solutionData.parameters.find(
    (parameter) => parameter.options?.subType === 'TECHNICALPOLICY'
  );
  const { datasetId, scenarioData } = action.payload;
  const technicalPolicyParameterValue = scenarioData.parametersValues.find(
    (parameter) => parameter.parameterId === technicalPolicyParameter.id
  )?.value;
  const parsedScenarioParameters = technicalPolicyParameterValue ? JSON.parse(technicalPolicyParameterValue) : [];
  const parsedScenarioParametersIds = parsedScenarioParameters.map((parameter) => parameter.parameterId);
  try {
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'LOADING',
    });
    let listGestionActivites;
    let niveauDePriorisationList;
    if (parsedScenarioParametersIds.includes('GestionActivites')) {
      const GestionActivitesValue = parsedScenarioParameters.find(
        (parameter) => parameter.parameterId === 'GestionActivites'
      ).value;
      listGestionActivites = yield call(downloadFileData, scenarioData.id, { value: GestionActivitesValue });
    } else {
      const { data } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
        query: 'MATCH (n: GestionActivites) RETURN n',
      });
      listGestionActivites = data.map((node) => node.n.properties) ?? [];
    }

    if (parsedScenarioParametersIds.includes('NiveauDePriorisation')) {
      const GestionActivitesValue = parsedScenarioParameters.find(
        (parameter) => parameter.parameterId === 'NiveauDePriorisation'
      ).value;
      niveauDePriorisationList = yield call(downloadFileData, scenarioData.id, { value: GestionActivitesValue });
    } else {
      const { data } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
        query: 'MATCH (n: NiveauDePriorisation) RETURN n',
      });
      niveauDePriorisationList = data.map((node) => node.n.properties) ?? [];
    }

    const { data: familleEquipement } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
      query: 'MATCH (n: FamilleEquipement) RETURN n',
    });
    const { data: hierarchieDesEquipements } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
      query: 'MATCH (n: HierarchieDesEquipements) RETURN n',
    });

    const familleEquipmentList = familleEquipement.map((node) => node.n.properties);
    const hierarchieDesEquipementsList = hierarchieDesEquipements.map((node) => node.n.properties);
    const equipmentFamiliesMapping = WizardUtils.buildEquipmentFamiliesMapping(hierarchieDesEquipementsList);

    const technicalPolicy = {
      familleEquipement: familleEquipmentList,
      niveauDePriorisation: niveauDePriorisationList,
      gestionActivites: listGestionActivites,
      hierarchieDesEquipements: hierarchieDesEquipementsList,
      equipmentFamiliesMapping,
      gisements: {},
    };
    if (listGestionActivites.length > 0) {
      const queries = [];
      const files = [];
      listGestionActivites.forEach((activity) => {
        Object.assign(technicalPolicy.gisements, { [activity.Gisement]: { tableNamesMapping: activity } });
      });
      for (const activity of listGestionActivites) {
        for (const [key, value] of Object.entries(activity)) {
          if (['ReglesGestion', 'Gisement'].includes(key)) {
            if (parsedScenarioParametersIds.includes(value)) {
              const parameterValue = parsedScenarioParameters.find(
                (parameter) => parameter.parameterId === value
              ).value;
              files.push({
                value: parameterValue,
                gisementName: activity.Gisement,
                key,
              });
            } else
              queries.push({
                queryString: WizardUtils.buildQueryString(value),
                gisementName: activity.Gisement,
                key,
              });
          } else if (key === 'ClasseActif') {
            // specifically for ClasseActif, we don't query the entire table and we need to use two types of queries
            // depending on dataset
            queries.push({
              queryString: WizardUtils.buildReducedClasseActifQueryString(value),
              gisementName: activity.Gisement,
              key,
              value,
            });
            // TODO: RMZ-1010
          } else if (key !== 'id') {
            technicalPolicy.gisements[activity.Gisement] = {
              ...technicalPolicy.gisements[activity.Gisement],
              [key]: null,
            };
          }
        }
      }
      yield put({
        type: WIZARD_TWINGRAPH_QUERIES.SET_GANTT_QUERIES_RESULTS,
        payload: { scenarioId: scenarioData.id, technicalPolicy },
      });
      yield all(
        queries.map((query, index) => {
          if (index !== 0) delay(TWINGRAPH_QUERIES_DELAY);
          return call(getOneQuery, datasetId, scenarioData.id, query);
        })
      );
      yield all(
        files.map((file, index) => {
          if (index !== 0) delay(TWINGRAPH_QUERIES_DELAY);
          return call(downloadFileData, scenarioData.id, file);
        })
      );
    } else {
      yield put({
        type: WIZARD_TWINGRAPH_QUERIES.SET_GANTT_QUERIES_RESULTS,
        payload: { scenarioId: scenarioData.id, technicalPolicy },
      });
    }
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'IDLE',
    });
  } catch (error) {
    console.error(error);
  }
}

function* ganttChartTwingraphQueriesSaga() {
  yield takeEvery(WIZARD_TWINGRAPH_QUERIES.INITIALIZE_GANTT_CHART_TWINGRAPH_QUERIES, getGanttChartTwingraphQueries);
}
export default ganttChartTwingraphQueriesSaga;
